import { useCallback, useEffect, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { VerifyEmailRequest } from '@/api';
import { useOpenApi } from '@/common/providers/open-api-provider';
import { getFallbackErrorKey, isApiError } from '@/common/util';
import { getAppCheckTokenHeaders, isFirebaseError } from '@/firebase';
import { UIToast } from '@/ui';
import { useMeTranslation } from '../hooks';

type UseVerifyNewEmailActionData = {
    verifyNewEmail: (data: VerifyEmailRequest) => Promise<void>;
    loading: boolean;
    error: string;
};

/** Confirming a changed email address from the link sent to it. */
export function useVerifyNewEmailAction(): UseVerifyNewEmailActionData {
    const { t } = useMeTranslation();
    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });

    // One place that turns a failure into a message, so the toast and the
    // returned `error` can never disagree. Every error `POST /v3/auth/verify-new-email`
    // declares gets a branch.
    const getErrorMessage = useCallback(
        (error: unknown): string => {
            // Thrown before the request, while minting the App Check token.
            if (isFirebaseError(error)) {
                return t('shared.errors.app_check_failed');
            }

            if (isApiError(error, 'APP_CHECK_FAILED')) {
                return t('shared.errors.app_check_failed');
            }
            if (isApiError(error, 'INCORRECT_TOKEN')) {
                return t('shared.errors.incorrect_token');
            }
            if (isApiError(error, 'TOO_MANY_REQUESTS')) {
                return t('shared.errors.too_many_requests');
            }
            if (isApiError(error, 'BAD_REQUEST')) {
                return error.response?.data.message.join(', ') ?? '';
            }

            return t(getFallbackErrorKey(error));
        },
        [t]
    );

    const mutation = useMutation({
        mutationFn: ({ token }: VerifyEmailRequest) =>
            // The endpoint is App Check protected, so the token has to be
            // fetched before the request rather than added by an interceptor.
            getAppCheckTokenHeaders()
                .then((headers) =>
                    openApiRef.current.auth.verifyNewEmail(
                        { token },
                        { headers }
                    )
                )
                .then(() => {}),
        onSuccess: () => {
            UIToast.success(t('actions.verify_new_email.success'));
        },
        onError: (error) => {
            UIToast.error(getErrorMessage(error));
        }
    });

    return {
        verifyNewEmail: (data) => mutation.mutateAsync(data),
        loading: mutation.isPending,
        error: mutation.error ? getErrorMessage(mutation.error) : ''
    };
}
